/**
 * Component for configuring auto top-up settings.
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Zap, AlertCircle, Loader2 } from "lucide-react";
import { usePayment } from "../lib/PaymentContext";
import { useIsWorkspaceAdmin } from "../hooks/useWorkspaceMembers";
import { BOOST_PACKS } from "../lib/stripe";

interface AutoTopUpSettingsProps {
  onAddPaymentMethod?: () => void;
  hasPaymentMethod?: boolean;
}

export default function AutoTopUpSettings({
  onAddPaymentMethod,
  hasPaymentMethod: hasPaymentMethodProp,
}: AutoTopUpSettingsProps) {
  const { t } = useTranslation();
  const { autoTopUp, hasPaymentMethod: hasPaymentMethodContext, updateAutoTopUp, loading } = usePayment();
  // Auto top-up picks who pays for the workspace: the server takes it from an admin only.
  const isAdmin = useIsWorkspaceAdmin();

  // Use prop if provided, otherwise fall back to context
  const hasPaymentMethod = hasPaymentMethodProp ?? hasPaymentMethodContext;

  const [enabled, setEnabled] = useState(autoTopUp?.enabled ?? false);
  const [threshold, setThreshold] = useState(autoTopUp?.threshold ?? 100);
  const [topUpPriceId, setTopUpPriceId] = useState(
    autoTopUp?.topUpPriceId ?? BOOST_PACKS[1]?.id ?? ""
  );
  const [monthlyBudget, setMonthlyBudget] = useState(
    autoTopUp?.monthlyBudget ?? 5000
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when autoTopUp changes
  useEffect(() => {
    if (autoTopUp) {
      setEnabled(autoTopUp.enabled ?? false);
      setThreshold(autoTopUp.threshold ?? 100);
      setTopUpPriceId(autoTopUp.topUpPriceId || BOOST_PACKS[1]?.id || "");
      setMonthlyBudget(autoTopUp.monthlyBudget ?? 5000);
    }
  }, [autoTopUp]);

  // Track changes
  useEffect(() => {
    const changed =
      enabled !== (autoTopUp?.enabled ?? false) ||
      threshold !== (autoTopUp?.threshold ?? 100) ||
      topUpPriceId !== (autoTopUp?.topUpPriceId ?? "") ||
      monthlyBudget !== (autoTopUp?.monthlyBudget ?? 5000);
    setHasChanges(changed);
  }, [enabled, threshold, topUpPriceId, monthlyBudget, autoTopUp]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await updateAutoTopUp({
        enabled,
        threshold,
        topUpPriceId,
        monthlyBudget,
      });
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedPack = BOOST_PACKS.find((p) => p.id === topUpPriceId);
  const budgetUsedPercent = autoTopUp
    ? Math.min(100, ((autoTopUp.currentMonthSpend ?? 0) / (autoTopUp.monthlyBudget || 5000)) * 100)
    : 0;

  // Don't block render with loading skeleton - show UI immediately
  // Just disable controls while loading
  const isDisabled = loading || !hasPaymentMethod || !isAdmin;

  return (
    <div className="bg-card rounded-xl border p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-500" />
          <h3 className="font-semibold">Auto Top-Up</h3>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={isDisabled}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50" />
        </label>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Automatically purchase boosts when your balance falls below a threshold.
      </p>

      {!isAdmin && <p className="text-sm text-muted-foreground mb-4">{t("billing.autoTopUpAdminOnly")}</p>}

      {!hasPaymentMethod && !loading && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-800">
              Add a payment method to enable auto top-up.
            </p>
            {onAddPaymentMethod && (
              <button
                onClick={onAddPaymentMethod}
                className="text-sm text-yellow-700 dark:text-yellow-300 underline mt-1"
              >
                Add payment method
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600 text-sm mb-4">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Threshold */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Trigger when balance falls below
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value) || 0)}
              min={10}
              max={10000}
              disabled={!enabled || !isAdmin}
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-muted disabled:text-muted-foreground"
            />
            <span className="text-muted-foreground">boosts</span>
          </div>
        </div>

        {/* Boost Pack Selection */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Boost pack to purchase
          </label>
          <select
            value={topUpPriceId}
            onChange={(e) => setTopUpPriceId(e.target.value)}
            disabled={!enabled || !isAdmin}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-muted disabled:text-muted-foreground"
          >
            {BOOST_PACKS.map((pack) => (
              <option key={pack.id} value={pack.id}>
                {pack.name} - {pack.boosts.toLocaleString()} boosts (
                {pack.priceDisplay})
              </option>
            ))}
          </select>
        </div>

        {/* Monthly Budget */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Monthly spending limit
          </label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input
              type="number"
              value={monthlyBudget / 100}
              onChange={(e) =>
                setMonthlyBudget((parseFloat(e.target.value) || 0) * 100)
              }
              min={5}
              max={1000}
              step={5}
              disabled={!enabled || !isAdmin}
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-muted disabled:text-muted-foreground"
            />
            <span className="text-muted-foreground">/month</span>
          </div>
        </div>

        {/* Budget Progress */}
        {autoTopUp?.enabled && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">This month's spending</span>
              <span className="font-medium">
                ${((autoTopUp.currentMonthSpend ?? 0) / 100).toFixed(2)} / $
                {((autoTopUp.monthlyBudget ?? 5000) / 100).toFixed(2)}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  budgetUsedPercent >= 90
                    ? "bg-red-500"
                    : budgetUsedPercent >= 70
                      ? "bg-yellow-500"
                      : "bg-green-500"
                }`}
                style={{ width: `${budgetUsedPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Summary */}
        {enabled && selectedPack && (
          <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800">
              When your balance drops below{" "}
              <strong>{(threshold ?? 100).toLocaleString()} boosts</strong>, we'll
              automatically charge <strong>{selectedPack.priceDisplay}</strong>{" "}
              to add <strong>{selectedPack.boosts.toLocaleString()} boosts</strong>,
              up to <strong>${((monthlyBudget ?? 5000) / 100).toFixed(2)}</strong> per
              month.
            </p>
          </div>
        )}

        {/* Save Button */}
        {hasChanges && isAdmin && (
          <button
            onClick={handleSave}
            disabled={isSaving || (!hasPaymentMethod && enabled)}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        )}
      </div>
    </div>
  );
}
